/* eslint-disable react-hooks/exhaustive-deps */
import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import VirtualizedCategoryTypeCarousel from '@/components/feature/category-type/category-type-carousel/VirtualizedCategoryTypeCarousel';
import CategoryTypePoiModal from '@/components/feature/category-type/category-type-poi-modal';
import CategoryTypeSearch from '@/components/feature/category-type/category-type-search';
import { BuildingMenuType } from '@/enums';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore, useCategoryTypeStore } from '@/stores';
import type { PoiContentSlide } from '@/types/shop';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './CategoryTypePage.module.scss';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import { FloorGuideType } from '@/enums/floor-guide';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { CustomMenuCategoryCode } from '@/constants/custom-menu-category-code';
import { useCurrentCustomMenu } from '@/hooks/custom-menu';
import FacilityCategoryTypePage from './facility/FacilityCategoryTypePage';
import ParkingPage from './parking';

const CategoryTypePage = () => {
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();

    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);
    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();

    const categoryTypeContents = useCategoryTypeStore((state) => state.categoryTypeContents);
    const rawCategoryTypeContents = useCategoryTypeStore((state) => state.rawCategoryTypeContents);
    const currentMode = useCategoryTypeStore((state) => state.currentMode);
    const updateCategoryTypeContents = useCategoryTypeStore((state) => state.updateCategoryTypeContents);
    const initialize = useCategoryTypeStore((state) => state.initialize);
    const setTranslationFunction = useCategoryTypeStore((state) => state.setTranslationFunction);
    const resetFilters = useCategoryTypeStore((state) => state.resetFilters);
    const setMenuId = useCategoryTypeStore((state) => state.setMenuId);

    const [isOpenPoiModal, setIsOpenPoiModal] = useState(false);
    const [poiContent, setPoiContent] = useState<PoiContentSlide | null>(null);
    const { setSelectedPoi, setFloorGuideType } = useFloorGuideStore();
    const navigate = useNavigate();

    const isInitializedRef = useRef(false);
    const prevLanguageRef = useRef(ui.language);

    useEffect(() => {
        if (gnbMenuId) {
            setMenuId(gnbMenuId);
        }
    }, [gnbMenuId]);

    useEffect(() => {
        if (!isInitializedRef.current && gnbMenuId) {
            resetFilters();
            initialize(t);
            isInitializedRef.current = true;
            TrackingService.clearSearchKeyword(gnbMenuId);
        }
    }, [t, server?.buildingMenus, gnbMenuId]);

    useEffect(() => {
        if (prevLanguageRef.current !== ui.language) {
            setTranslationFunction(t);
            prevLanguageRef.current = ui.language;
        }
    }, [t, ui.language]);

    useEffect(() => {
        if (rawCategoryTypeContents.length > 0) {
            updateCategoryTypeContents(currentMode);
        }
    }, [rawCategoryTypeContents, currentMode]);

    const isVerticalFullscreen = !isHorizontal && ui.screenMode !== 'split';

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        if (gnbMenuId) {
            return listMenus.find((menu) => menu.id === gnbMenuId && menu.menuType === BuildingMenuType.CUSTOM_MENU);
        }
        return listMenus.find((menu) => menu.menuType === BuildingMenuType.CUSTOM_MENU);
    }, [server?.buildingMenus, ui.language, gnbMenuId]);

    const pageClassname = useMemo(() => {
        return clsx(styles['category-type'], {
            [styles['category-type__vertical']]: !isHorizontal,
            [styles['category-type__horizontal']]: isHorizontal,
            [styles['category-type__contrast']]: ui.contrast,
            [styles['category-type__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['category-type__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
        });
    }, [ui.screenMode, ui.contrast, isHorizontal]);

    const carouselClassname = useMemo(() => {
        return clsx(styles['category-type__carousel'], {
            [styles['category-type__carousel__horizontal']]: isHorizontal,
            [styles['category-type__carousel__vertical']]: !isHorizontal,
            [styles['category-type__carousel__contrast']]: ui.contrast,
            [styles['category-type__carousel__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['category-type__carousel__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            [styles['category-type__carousel__vertical__empty']]: categoryTypeContents.length === 0 && !isHorizontal,
            [styles['category-type__carousel__vertical__empty__halfmode']]: categoryTypeContents.length === 0 && !isHorizontal && ui.screenMode === 'split',
            [styles['category-type__carousel__horizontal__empty']]: categoryTypeContents.length === 0 && isHorizontal,
            [styles['category-type__carousel__horizontal__empty__halfmode']]: categoryTypeContents.length === 0 && isHorizontal && ui.screenMode === 'split',
        });
    }, [isHorizontal, ui.screenMode, ui.contrast, categoryTypeContents.length]);

    const { menuCode } = useCurrentCustomMenu(gnbMenuId);

    switch (menuCode) {
        case CustomMenuCategoryCode.FACILITY:
            return <FacilityCategoryTypePage gnbMenuId={gnbMenuId} />;

        case CustomMenuCategoryCode.PARKING:
            return <ParkingPage gnbMenuId={gnbMenuId} />;
        default:
            break;
    }

    const onClickPoiContent = (data: PoiContentSlide) => {
        setPoiContent(data);
        setIsOpenPoiModal(true);
        TrackingService.trackPoi(data.data.id, undefined, data.data);
    };

    const handleLocationCheck = () => {
        if (!poiContent) return;
        setSelectedPoi({
            studioFloorID: poiContent.data.studioFloorId,
            studioPoiId: poiContent.data.studioPoiId,
            buildingId: poiContent.data.studioBuildingId,
            contentId: poiContent.data.id,
        });
        setFloorGuideType(FloorGuideType.MARKER);
        navigate(PATHS.FLOOR_GUIDE.CURRENT_LOCATION(poiContent.data.studioFloorId, poiContent.data.studioPoiId));
    };

    const handleGuideStart = () => {
        if (!poiContent) return;
        setSelectedPoi({
            studioFloorID: poiContent.data.studioFloorId,
            studioPoiId: poiContent.data.studioPoiId,
            buildingId: poiContent.data.studioBuildingId,
            contentId: poiContent.data.id,
        });
        setFloorGuideType(FloorGuideType.DIRECTION);
        navigate(PATHS.FLOOR_GUIDE.DIRECTION_GUIDE(poiContent.data.studioFloorId, poiContent.data.studioPoiId));
    };

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['category-type__header'], {
                    [styles['category-type__header--horizontal']]: isHorizontal,
                    [styles['category-type__header--vertical']]: !isHorizontal,
                    [styles['category-type__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader
                    title={(currentMenu?.name as string) || ''}
                    description={currentMenu?.description || ''}
                    isHorizontal={isHorizontal}
                    tabIndex={1}
                />
            </div>
            <div className={carouselClassname}>
                {categoryTypeContents.length > 0 ? (
                    <VirtualizedCategoryTypeCarousel contents={categoryTypeContents} onClickPoiContent={onClickPoiContent} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="category-type-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        textEmpty={t('custom_menu.empty')}
                        ariaLabel={t('custom_menu.empty')}
                    />
                )}
            </div>

            <div
                className={clsx(styles['category-type__search'], {
                    [styles['category-type__search--horizontal']]: isHorizontal,
                    [styles['category-type__search--vertical']]: !isHorizontal,
                    [styles['category-type__search--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                    [styles['category-type__search--vertical__fullscreen']]: isVerticalFullscreen,
                })}
            >
                <CategoryTypeSearch isVerticalFullscreen={isVerticalFullscreen} menuId={gnbMenuId} />
            </div>

            {isOpenPoiModal && (
                <CategoryTypePoiModal
                    isOpen={isOpenPoiModal}
                    language={ui.language}
                    poiContent={poiContent}
                    buildings={server?.store.buildings || []}
                    screenMode={ui.screenMode}
                    onClose={() => setIsOpenPoiModal(false)}
                    onGuideStart={handleGuideStart}
                    onLocationCheck={handleLocationCheck}
                />
            )}
        </div>
    );
};

export default CategoryTypePage;
